/**
 * Fetch-based SSE reader for endpoints that require Authorization headers.
 * Native EventSource cannot send Bearer tokens — use this instead.
 */

export async function readNamedSseStream(
  response: Response,
  onChunk: (eventName: string | undefined, data: unknown) => void,
): Promise<void> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processChunks = (rawBuffer: string, flushTrailing: boolean): string => {
    const normalized = rawBuffer.replace(/\r\n/g, '\n');
    const parts = normalized.split('\n\n');
    const trailing = flushTrailing ? '' : (parts.pop() ?? '');
    const completeChunks = flushTrailing
      ? parts.filter((part) => part.trim().length > 0)
      : parts;

    for (const chunk of completeChunks) {
      const lines = chunk.split('\n');
      const eventLine = lines.find((line) => line.startsWith('event:'));
      const eventName = eventLine ? eventLine.slice(6).trim() : undefined;
      const dataLines = lines
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim());
      const raw = dataLines.join('\n');
      if (!raw) {
        if (eventName) onChunk(eventName, null);
        continue;
      }
      try {
        onChunk(eventName, JSON.parse(raw) as unknown);
      } catch {
        // Ignore keep-alive / partial non-JSON chunks.
      }
    }

    return trailing;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      if (buffer.trim()) {
        processChunks(buffer, true);
      }
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    buffer = processChunks(buffer, false);
  }
}
